import { convertWithRates, useFxRates, type FxRatesPerEur } from "@/lib/fxRates";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PriceLabel = "good" | "market" | "above";

export interface PriceBenchmark {
  avg_price: number;
  p25: number;
  p50: number;
  p75: number;
  sample_size: number;
}

export interface PriceBenchmarkResult {
  benchmark: PriceBenchmark | null;
  label: PriceLabel | null;
  /** Percentage below avg (positive = cheaper than avg, negative = more expensive). */
  diffPct: number | null;
}

interface Params {
  make?: string | null;
  model?: string | null;
  year?: number | null;
  mileage?: number | null;
  price?: number | null;
  countryCode?: string | null;
  currency?: string | null;
}

// fx_rates stores units per 1 EUR (GBP 0.8547 ⇒ 1 GBP ≈ 1.17 EUR), so EUR = price / rate.
const toEur = (price: number, currency: string | null | undefined, rates: FxRatesPerEur | undefined): number | null =>
  convertWithRates(price, (currency || "EUR").toUpperCase(), "EUR", rates);

export const computePriceLabel = (
  priceEur: number,
  b: PriceBenchmark
): { label: PriceLabel; diffPct: number } => {
  const diffPct = ((b.avg_price - priceEur) / b.avg_price) * 100;
  let label: PriceLabel;
  if (priceEur < Number(b.p25)) label = "good";
  else if (priceEur <= Number(b.p75)) label = "market";
  else label = "above";
  return { label, diffPct };
};

export const usePriceBenchmark = ({
  make,
  model,
  year,
  mileage,
  price,
  countryCode,
  currency,
}: Params): PriceBenchmarkResult => {
  const enabled =
    !!make && !!model && !!year && mileage != null && mileage > 0 && !!price;

  const { data: fxRates } = useFxRates();
  const { data } = useQuery({
    queryKey: ["price-benchmark", make, model, year, mileage, countryCode ?? "RO"],
    enabled,
    staleTime: 1000 * 60 * 60, // 1h client cache
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_price_benchmark", {
        p_make: make!,
        p_model: model!,
        p_year: year!,
        p_mileage: mileage!,
        p_country: (countryCode ?? "RO").toUpperCase(),
      });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as PriceBenchmark) ?? null;
    },
  });

  if (!data || !price) return { benchmark: null, label: null, diffPct: null };
  const priceEur = toEur(price, currency, fxRates);
  if (priceEur === null) return { benchmark: data, label: null, diffPct: null };
  const { label, diffPct } = computePriceLabel(priceEur, data);
  return { benchmark: data, label, diffPct };
};
